<script setup>
import { targetStatuses } from 'boot/globals';
import { i18n } from 'boot/i18n';
import TrialByTrialData from 'components/Programs/DataCollection/TrialByTrialData';
import {
  HrContentCard,
  HrDivider,
  HrFormSectionHeader,
  HrP,
} from 'src/design-system';
import { promptLevels as usePromptLevelsStore } from 'src/store/promptLevels';
import { sessions as useSessionsStore } from 'src/store/sessions';
import { isBehaviorTrackingProgram } from 'src/utils/ProgramUtils';
import { workflows as useWorkflowsStore } from 'src/store/workflows';
import { getTargetType } from 'src/utils/TargetUtils';
import { computed, ref } from 'vue';

/**
 * Picker over the session's Behavior Tracking targets, grouped by program. Each target is
 * the card a session shows for it, so a tap asks for and records exactly what scoring that target
 * in the session would. The answers are held as pending trials, which is also what each card counts
 * and colors its badge by, until the dialog is saved.
 *
 * Shared by the stand-alone dialog and the press-and-hold modal.
 */
const emit = defineEmits({
  /**
   * Fired whenever an answer is recorded, with every answer so far as
   * `[{ program, target, count, promptLevel?, scoreValue?, promptLevelTemplate? }]`.
   */
  update: null,
});

// Only data types scored by a tap; timers, rates and sub-targets need inputs of their own.
const TAP_RECORDED_DATA_TYPES = ['discreteTrial', 'frequency'];
// Shows text only, so the session records nothing for it and its absence needs no explaining.
const NON_RECORDING_DATA_TYPES = ['instructions'];

const sessionsStore = useSessionsStore();
const promptLevelsStore = usePromptLevelsStore();
const workflowsStore = useWorkflowsStore();

const pendingTrials = ref([]);
const pendingData = computed(() => ({ data: pendingTrials.value }));

/**
 * Behavior Tracking programs in this session, with the targets a tap can record. A program that also
 * has targets recorded only on its card says which types can be recorded here, so the rest are
 * explained rather than silently absent.
 */
const behaviorPrograms = computed(() => sessionsStore.activeSessionPrograms
  .filter((sessionProgram) => isBehaviorTrackingProgram(sessionProgram?.program))
  .map((sessionProgram) => {
    // Sub-targets belong to their parent's card, so only top-level targets are considered here.
    const collecting = (sessionProgram.program.targets ?? [])
      .filter((target) => !target.parentTarget && targetStatuses[target.status]?.canCollectData);
    const typeOf = (target) => getTargetType(target, sessionProgram.program);

    return {
      sessionProgram,
      targets: collecting.filter((target) => TAP_RECORDED_DATA_TYPES.includes(typeOf(target))),
      hasTargetsRecordedOnCard: collecting
        .some((target) => ![...TAP_RECORDED_DATA_TYPES, ...NON_RECORDING_DATA_TYPES].includes(typeOf(target))),
    };
  })
  .filter(({ targets, hasTargetsRecordedOnCard }) => targets.length > 0 || hasTargetsRecordedOnCard));

// Resolved here rather than via targetDataTypes, whose labels are translated once at module load.
const tapRecordedTypeLabels = computed(() => TAP_RECORDED_DATA_TYPES
  .map((type) => i18n.t(`programs.targets.dataTypes.${type}`)).join(', '));

/** Collapses the pending trials into one tally per program, target and answer. */
const toOccurrences = (trials) => {
  const tallies = new Map();

  trials.forEach(({
    program, target, promptLevel, scoreValue, promptLevelTemplate,
  }) => {
    const key = [program, target, promptLevel, promptLevelTemplate].join('|');
    if (!tallies.has(key)) {
      tallies.set(key, {
        program,
        target,
        count: 0,
        ...(promptLevel && { promptLevel, scoreValue }),
        ...(promptLevelTemplate && { promptLevelTemplate }),
      });
    }
    tallies.get(key).count += 1;
  });

  return [...tallies.values()];
};

const onRecord = (sessionProgram, target, data) => {
  pendingTrials.value = [...pendingTrials.value, {
    // A trial number is what makes a card count the entry in its badge.
    trial: pendingTrials.value.length + 1,
    program: sessionProgram.program.id,
    target: target.id,
    promptLevel: data.promptLevel ?? null,
    scoreValue: data.scoreValue ?? null,
    ...(data.promptLevelTemplate && { promptLevelTemplate: data.promptLevelTemplate }),
  }];

  emit('update', toOccurrences(pendingTrials.value));
};
</script>

<template>
  <div>
    <hr-form-section-header
      :label="i18n.t('run.collectData.addBehaviorDialog.heading')"
      class="q-pb-md"
    />

    <hr-content-card>
      <template v-if="behaviorPrograms.length > 0">
        <div
          v-for="({ sessionProgram, targets, hasTargetsRecordedOnCard }, index) in behaviorPrograms"
          :key="sessionProgram.program.id"
        >
          <hr-divider
            v-if="index > 0"
            class="q-mt-lg q-mb-md"
          />
          <hr-form-section-header
            :label="sessionProgram.program.programName"
            class="q-pb-md"
          />

          <div
            v-if="targets.length > 0"
            class="q-pt-sm"
          >
            <div class="row q-col-gutter-md">
              <div
                v-for="target in targets"
                :key="target.id"
                class="col-6"
                :data-e2e="`chip-behavior-target-${target.id}`"
              >
                <trial-by-trial-data
                  :session-program="sessionProgram"
                  :target="target"
                  :data="pendingData"
                  :active-prompt-levels="promptLevelsStore.activePromptLevels"
                  :workflow-statuses="workflowsStore.workflowStatuses"
                  dense
                  @record="onRecord(sessionProgram, target, $event)"
                />
              </div>
            </div>
          </div>
          <hr-p
            v-if="hasTargetsRecordedOnCard"
            class="hr-body-small hr-text-on-surface-variant q-mb-none q-pt-sm"
            :data-e2e="`text-recorded-on-card-${sessionProgram.program.id}`"
          >
            {{ i18n.t('run.collectData.addBehaviorDialog.textRecordedOnProgramCard', {
              types: tapRecordedTypeLabels,
            }) }}
          </hr-p>
        </div>
      </template>

      <hr-p
        v-else
        class="q-mb-none"
      >
        {{ i18n.t('run.collectData.addBehaviorDialog.textNoBehaviorPrograms') }}
      </hr-p>
    </hr-content-card>
  </div>
</template>
